import * as analytics from "@/lib/analytics";

const API_KEY = import.meta.env.REACT_APP_COOKIE_CONTROL_API_KEY;

// PINNED, deliberately — `9.x` floats to whatever CIVIC publishes next.
//
// We reshape this panel with our own CSS (see the Cookie Control block at the end of
// index.css), and those rules are written against markup we do not control: #cc-panel
// being absolutely positioned, the slideout class names, the id of the button holder.
// A floating version means CIVIC can restructure that markup on any Tuesday and our
// overrides break silently — on a consent surface, where "broken" can mean the Accept
// button is off-screen and nobody can consent at all.
//
// The cost is that security and compliance fixes no longer arrive on their own, so this
// needs a periodic bump: check the changelog, deploy, confirm the bar still renders.
export const CDN_VERSION = "9.11";
export const CDN_URL = `https://cc.cdn.civiccomputing.com/9/cookieControl-${CDN_VERSION}.min.js`;

// PostHog's own storage. Cookie Control deletes every cookie it does not recognise each
// time preferences change, so these must be declared against the category that owns them
// or the analytics cookie is swept moments after the visitor opted in.
//
// Nothing else of ours is at risk from that sweep: it touches cookies only, so the
// IndexedDB upload queue (pending lesson audio) and the sessionStorage ToS flag are
// outside its remit.
const ANALYTICS_COOKIES = ["ph_*", "__ph_opt_in_out_*"];

// Routes that deliberately get NO banner.
//
// These are the tokenized consent pages a parent reaches from an email. Someone on one of
// them is deciding whether their child may be recorded, and stacking a second, unrelated
// consent dialog on top of that moment is confusing at best. Skipping them also means no
// analytics there, which is the safer direction anyway.
//
// `/consent/` keeps its trailing slash on purpose: `/consent-ledger` is the instructor's
// own authenticated page inside the app shell and must behave like any other route.
const EXCLUDED_PATHS = [
  "/consent/", // /consent/:token and /consent/revoke/:token
  "/recording-consent", // public tokenless render of the agreement
];

// Set on <html> once a visitor has actually answered, which is what hides CIVIC's
// corner icon (see index.css). Kept as an attribute rather than component state
// because the thing it styles is third-party DOM outside React entirely.
const RESPONDED_ATTR = "data-cc-responded";

/**
 * Has the visitor actually ANSWERED, as opposed to merely seen the bar?
 *
 * Read off Cookie Control's own record, whose three states are distinguishable and
 * were confirmed against production: Accept writes
 * `optionalCookies: {analytics: "accepted"}`, Decline writes `{analytics: "revoked"}`,
 * and dismissing with the X writes `{}`.
 *
 * So a non-empty map is the discriminator, and dismissal deliberately does NOT count
 * — somebody who closed the bar without choosing still needs a way back to it, and
 * that way is the icon this flag hides.
 */
export function hasRespondedToConsent() {
  if (typeof document === "undefined") return false;
  const raw = (document.cookie.match(/(?:^|;\s*)CookieControl=([^;]*)/) || [])[1];
  if (!raw) return false;
  let parsed;
  try {
    parsed = JSON.parse(decodeURIComponent(raw));
  } catch {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return false; // unreadable record — treat as unanswered and keep the icon
    }
  }
  return Object.keys(parsed?.optionalCookies || {}).length > 0;
}

/** Mirrors {@link hasRespondedToConsent} onto <html> for the CSS to key off. */
export function syncRespondedFlag() {
  if (typeof document === "undefined") return false;
  const responded = hasRespondedToConsent();
  document.documentElement.toggleAttribute(RESPONDED_ATTR, responded);
  return responded;
}

/**
 * Re-sync the flag whenever Cookie Control opens or closes the bar.
 *
 * The category `onAccept`/`onRevoke` callbacks are NOT sufficient on their own, which
 * cost a production round-trip to find: CIVIC fires them only on an actual
 * TRANSITION. Declining when the category was already off — i.e. every first-time
 * visitor who says no — changes nothing, so no callback runs, and the flag stayed
 * false while the cookie plainly read `{analytics: "revoked"}`.
 *
 * The DOM does not have that problem. The bar is added and removed as a direct child
 * of #ccc on every open and close, so watching that one list catches accept, decline,
 * dismiss and reopen alike, without depending on which callbacks CIVIC chooses to
 * fire. The cookie stays the source of truth; this only decides when to re-read it.
 */
export function observeConsentResponse() {
  if (typeof MutationObserver === "undefined") return null;

  const attach = (root) => {
    const observer = new MutationObserver(() => syncRespondedFlag());
    observer.observe(root, { childList: true });
    return observer;
  };

  const existing = document.getElementById("ccc");
  if (existing) return attach(existing);

  // #ccc is NOT there yet when the script finishes loading — `load()` fetches licence
  // config from CIVIC before it renders anything, so it is async despite looking like
  // a plain call. Attaching straight after `load()` silently observed nothing, and the
  // icon then only disappeared on the NEXT page load, which reads as an intermittent
  // glitch rather than a missing observer. So wait for it: #ccc is appended directly
  // to <body>, so a childList watch on body catches its arrival and then stands down.
  const bootstrap = new MutationObserver(() => {
    const root = document.getElementById("ccc");
    if (!root) return;
    bootstrap.disconnect();
    attach(root);
    syncRespondedFlag();
  });
  bootstrap.observe(document.body, { childList: true });
  return bootstrap;
}

/**
 * Reopen the preferences bar.
 *
 * This is the replacement for the corner icon once it is hidden, and it is not
 * optional decoration: withdrawing consent has to stay as easy as giving it, so
 * something has to lead back here. PrivacyPage carries it.
 */
export function openCookieSettings() {
  if (typeof window === "undefined") return false;
  if (typeof window.CookieControl?.open !== "function") return false;
  window.CookieControl.open();
  return true;
}

export function isExcludedPath(pathname = "") {
  return EXCLUDED_PATHS.some((path) =>
    path.endsWith("/")
      ? pathname.startsWith(path)
      : pathname === path || pathname.startsWith(`${path}/`),
  );
}

// dd/mm/yyyy, and it must track the "Last updated" date shown on PrivacyPage. Cookie
// Control invalidates stored consent whenever this moves, which is the point — a visitor
// who agreed to the old policy is asked again rather than silently carried over.
const PRIVACY_POLICY_UPDATED = "17/07/2026";

export function buildConfig() {
  return {
    apiKey: API_KEY,
    product: "COMMUNITY",

    // Community has no geolocation, so a single mode serves every visitor and GDPR is
    // the strict one: optional categories default to OFF and nothing fires before the
    // visitor accepts. Upgrading to PRO is what would allow CCPA opt-out in the US.
    mode: "GDPR",
    consentCookieExpiry: 90,

    // A light surface, restyled to Hans's palette in index.css. CIVIC's light theme is
    // the closer starting point of the two, so it fights the overrides less.
    theme: "light",

    // The per-category toggle is HIDDEN in CSS, which is safe only because there is
    // exactly ONE optional category: with one, Accept and Decline are precisely as
    // granular as the switch they replace. Add a second category and the switch has to
    // come back, or accept-all becomes the only route — the one thing a consent tool
    // must not do. cookieControlBar.test.js fails if a second category is added while
    // the toggle is still hidden.
    //
    // Note this changes what is HIDDEN, never what is DEFAULTED: mode "GDPR" still
    // starts every optional category off, and dismissing the bar with the X still
    // records nothing and leaves PostHog uninitialised. Only Accept grants.
    toggleType: "slider",

    // INERT ON COMMUNITY — kept deliberately. Asks for a slim bottom bar instead of the
    // 520px full-height slideout; the licence refuses it server-side and silently falls
    // back to the slideout.
    //
    // Proven rather than assumed: the deployed bundle demonstrably ships
    // `initialState:"notify"` and the DOM demonstrably renders `#ccc-module`, the slideout.
    // Do NOT try to re-test this from the console — `load()` is single-shot, so an in-page
    // reconfiguration is a silent no-op that reads as a pass (D8). CIVIC's Configuration
    // Preview shows the bar because it previews PRO, the same reason its Styling and TCF
    // tabs are greyed out on this account.
    //
    // It stays because switching `product` to "PRO" is then the entire upgrade — this line
    // and the notify copy below light up with no other change.
    initialState: "notify",

    // This one DOES work on Community, and it is the real intrusiveness win here: the
    // panel auto-opens on a visitor's first page view and never again — every subsequent
    // page is just the corner icon. Verified in production.
    //
    // Safe because dismissing is not consenting: the stored record comes back with
    // `optionalCookies: {}` and PostHog stays uninitialised, so a visitor who ignores the
    // panel is simply never tracked. Re-presenting it every page would nag without
    // changing that outcome. The corner icon is permanent for deciding later.
    notifyOnce: true,

    // EVERY STRING HERE IS SIZED TO THE BAR. The CSS lays the panel out in three
    // columns capped at min(20vh, 150px) and it must not scroll, so the copy is the
    // other half of that constraint — lengthen any of these and the bar either grows
    // or starts hiding its own content. CIVIC's defaults are what made it 245px: a
    // full-sentence H1 and a four-line paragraph about necessary cookies.
    //
    // Each line still has to earn its place. What a visitor must be told is that
    // sign-in cookies exist and are not optional, that analytics are optional and
    // off, and what analytics collect — which is what these say, minus the padding.
    text: {
      title: "Cookies",
      intro: "Essential cookies sign you in. Analytics are optional.",
      necessaryTitle: "Essential",
      necessaryDescription: "Sign-in only. Always on.",
      acceptSettings: "Accept",
      rejectSettings: "Decline",

      // Bottom bar (PRO only — inert here, see initialState above).
      notifyTitle: "Cookies",
      notifyDescription: "Essential cookies sign you in. Analytics are optional.",
      accept: "Accept",
      reject: "Decline",
    },

    // The panel is not a modal — there is nothing behind it the visitor must not touch,
    // and dimming the whole page to ask about analytics overstates the moment. Letting
    // the page keep scrolling is the same judgement.
    accessibility: {
      overlay: false,
      disableSiteScrolling: false,
    },

    // All six Hans session cookies are set server-side as httponly, which Cookie Control
    // protects automatically, so none of those need listing.
    //
    // Stripe's two are different: they are first-party and JS-readable, so the sweep WOULD
    // delete them on every preference change, taking Stripe's fraud signals with them
    // mid-checkout. They are declared necessary because fraud prevention on a payment flow
    // is — but that only holds while Stripe.js is confined to checkout, and it did not.
    // `@stripe/stripe-js`'s main entry injects js.stripe.com from module scope on IMPORT,
    // nothing here is code-split, and both checkout bodies imported it — so Stripe.js and
    // `__stripe_mid` were on the landing page regardless of the key. Both now go through
    // `lib/stripeClient`, which uses `@stripe/stripe-js/pure` (no import-time injection)
    // and calls loadStripe only at the checkout step. Keep it that way: a direct
    // `@stripe/stripe-js` import anywhere reinstates this, and `stripeClient.test.js`
    // fails the build if one appears.
    necessaryCookies: ["__stripe_mid", "__stripe_sid"],

    optionalCookies: [
      {
        name: "analytics",
        label: "Analytics",
        description: "Usage only — never your name or email.",
        cookies: ANALYTICS_COOKIES,
        // Either answer counts as answered — the flag is about whether a choice was
        // made, not which one — so both callbacks re-sync it.
        onAccept: () => {
          analytics.grantConsent();
          syncRespondedFlag();
        },
        onRevoke: () => {
          analytics.revokeConsent();
          syncRespondedFlag();
        },
      },
    ],

    statement: {
      // Sized to the bar like the strings above; CIVIC appends `name` as the link,
      // so this renders as "More in our Privacy Policy".
      description: "More in our",
      name: "Privacy Policy",
      url: "/privacy",
      updated: PRIVACY_POLICY_UPDATED,
    },
  };
}

/**
 * Load the consent banner.
 *
 * Deliberately fails CLOSED: with no API key the script never loads, so `grantConsent`
 * is never called and PostHog never starts. That covers both ways this goes wrong — QA
 * is unlicensed (Community covers a single domain) and consent scripts are a routine
 * ad-blocker target — and in each case the safe outcome is no analytics rather than
 * ungated analytics.
 *
 * @returns {boolean} whether a script tag was appended.
 */
export function initCookieConsent() {
  if (!API_KEY) return false;
  if (typeof document === "undefined") return false;
  // Evaluated once, at boot. That is the real-world path for these pages — they are opened
  // straight from a link in an email, never navigated to from inside the app — so a
  // client-side route change into one after the banner has already loaded is not a case
  // worth carrying a router subscription for.
  if (isExcludedPath(window.location?.pathname || "")) return false;
  if (document.querySelector(`script[src="${CDN_URL}"]`)) return false;

  // Set BEFORE the CDN script is appended, so a returning visitor never sees the icon
  // flash in and back out. The cookie is readable synchronously; CIVIC's script is not.
  syncRespondedFlag();

  const script = document.createElement("script");
  script.src = CDN_URL;
  script.async = true;
  script.onload = () => {
    window.CookieControl?.load(buildConfig());
    // Safe to call before #ccc exists — it waits for it. See the note there.
    observeConsentResponse();
  };
  document.body.appendChild(script);
  return true;
}
